/**
 * useTranslator — `t` for rendering, plus a stable handle for callbacks.
 *
 * WHY THIS EXISTS. Putting `t` in the dependency array of a data-loading
 * `useCallback` looks harmless and is not: the `t` returned by `useLanguage()`
 * is NOT referentially stable across renders (every component that calls
 * `useLanguage` re-applies `i18n.changeLanguage` on mount, which re-renders
 * every translation consumer with a fresh `t`). A loader that depends on `t`
 * therefore gets a new identity, its effect re-runs, it sets state, something
 * re-mounts, and the cycle repeats.
 *
 * That is not hypothetical — the share dialog shipped that way for an hour and
 * hammered `/share-targets` at ~11 requests a second, because its skeleton
 * mounting and unmounting was itself what churned `t`.
 *
 * The fix is to keep `t` out of loader dependencies. `tRef.current` is always
 * the latest translator, so error copy is still localized correctly, but the
 * loader's identity depends only on the data it actually reads.
 *
 *   const { t, tRef } = useTranslator();
 *   const load = useCallback(() => {
 *     api.get(id).catch(() => setError(tRef.current('x.loadFailed')));
 *   }, [id, tRef]);   // tRef is stable forever
 */

import { useRef } from 'react';
import { useLanguage } from '@/hooks/useLanguage';

export type Translator = (key: string, options?: any) => string;

export function useTranslator() {
  const { t } = useLanguage();
  const tRef = useRef<Translator>(t);
  // Assigned during render on purpose: a callback fired between this render and
  // the next effect flush must still see the current translator.
  tRef.current = t;
  return { t, tRef };
}

export default useTranslator;
